import React, { useEffect } from "react";

import classNames from "classnames";
import { X as CloseIcon } from "lucide-react";

import Button from "../button/Button";
import Tooltip from "../tooltip/Tooltip";
import { Drawer, DrawerContent, DrawerTitle } from "../ui/drawer";

import type { JSX } from "react";

import type { ButtonVariants } from "../button/Button";

export const SIDEPANEL_ANCHORS = {
  LEFT: "left",
  RIGHT: "right",
  TOP: "top",
  BOTTOM: "bottom",
} as const;

type SidePanelAnchorKeys = keyof typeof SIDEPANEL_ANCHORS;

type SidePanelAnchors = (typeof SIDEPANEL_ANCHORS)[SidePanelAnchorKeys];

interface SidePanelActions {
  label: string;
  id?: string | number;
  buttonVariant?: ButtonVariants;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isFullHeight?: boolean;
  buttonType?: "button" | "submit";
  tooltipOptions?: {
    id: string;
    content: string;
  };
  onClick?: () => void;
}

interface SidePanelProps {
  title: string | React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  anchor?: SidePanelAnchors;
  className?: string;
  children: React.ReactNode;
  actions?: SidePanelActions[];
  withoutPadding?: boolean;
}

export default function SidePanel({
  title,
  isOpen,
  onClose,
  anchor = SIDEPANEL_ANCHORS.RIGHT,
  className,
  children,
  actions,
  withoutPadding,
}: SidePanelProps): JSX.Element {
  useEffect(() => {
    // code pepite
    const getStartedButton = document.getElementsByClassName(
      "hlp-checklist-button",
    );
    if (getStartedButton.length > 0 && getStartedButton[0]) {
      if (isOpen) {
        getStartedButton[0].classList.add("hidden");
      } else {
        getStartedButton[0].classList.remove("hidden");
      }
    }
  }, [isOpen]);

  const renderActions = (): React.ReactNode =>
    actions?.map((action) => {
      const containerButtonClassNames = classNames("flex", {
        "w-full": action.isFullWidth,
      });
      return (
        <div
          key={action.id ?? action.label}
          className={containerButtonClassNames}
        >
          <Button
            data-tooltip-id={action?.tooltipOptions?.id}
            isDisabled={action.isDisabled}
            isFullHeight={action.isFullHeight}
            isFullWidth={action.isFullWidth}
            onClick={action.onClick}
            size="md"
            type={action.buttonType ?? "button"}
            variant={action.buttonVariant}
          >
            {action.label}
          </Button>
          {action.tooltipOptions ? (
            <Tooltip
              content={action.tooltipOptions.content}
              id={action.tooltipOptions.id}
            />
          ) : null}
        </div>
      );
    });

  const bodyClassNames = classNames(
    "flex-1 overflow-auto flex flex-col",
    withoutPadding ? "p-0" : "px-[1.09375rem] py-[1.40625rem]",
  );

  const isHorizontalAnchor =
    anchor === SIDEPANEL_ANCHORS.LEFT || anchor === SIDEPANEL_ANCHORS.RIGHT;

  const contentClassNames = classNames(
    "rounded-none bg-card p-0 [&>div:first-child]:hidden min-w-125 flex-col max-sm:w-full max-sm:min-w-fit",
    {
      "h-full": isHorizontalAnchor,
      "max-h-[80vh] w-full":
        anchor === SIDEPANEL_ANCHORS.TOP || anchor === SIDEPANEL_ANCHORS.BOTTOM,
    },
    className,
  );

  return (
    <Drawer
      autoFocus={false}
      direction={anchor}
      onOpenChange={(open) => !open && onClose()}
      open={isOpen}
    >
      <DrawerContent className={contentClassNames}>
        <div className="flex h-full flex-col">
          <div className="border-b-border flex justify-between border-b-2 px-[1.09375rem] py-[1.40625rem] text-[1.40625rem] font-medium">
            <DrawerTitle className="w-full text-[1.40625rem] font-medium text-inherit">
              {title}
            </DrawerTitle>
            <button
              aria-label="Close side panel"
              className="text-muted-foreground relative top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={onClose}
              type="button"
            >
              <CloseIcon />
            </button>
          </div>
          <div className={bodyClassNames}>{children}</div>
          {actions && actions.length > 0 ? (
            <div className="border-t-border border-t-2 p-[1.09375rem]">
              <div className="flex gap-[0.78125rem]">{renderActions()}</div>
            </div>
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
